"use client";

import React, { useRef, useState, useEffect, useCallback, useId } from "react";
import { Button } from "@/app/_module/components/ui/button";
import { LayoutDashboard, TrendingUp, Activity } from "lucide-react";

interface Tab {
  id: string;
  label: string;
  icon?: string;
}

interface TabNavigationProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  tabs: Tab[];
}

const iconMap: Record<string, React.ReactNode> = {
  dashboard: <LayoutDashboard className="h-4 w-4" />,
  status: <TrendingUp className="h-4 w-4" />,
  activity: <Activity className="h-4 w-4" />,
};

export const TabNavigation = function ({ activeTab, onTabChange, tabs }: TabNavigationProps) {
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({
    opacity: 0,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId();

  const updateIndicator = useCallback((skipTransition = false) => {
    const nav = navRef.current;
    if (!nav) return;

    // Find the active tab button
    const activeButton = nav.querySelector(`[data-tab-active="true"]`) as HTMLElement;

    if (activeButton) {
      const navRect = nav.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      const scrollLeft = nav.scrollLeft || 0;
      const offsetLeft = buttonRect.left - navRect.left + scrollLeft;

      setIndicatorStyle({
        width: buttonRect.width,
        height: buttonRect.height,
        transform: `translateX(${offsetLeft}px)`,
        opacity: 1,
      });

      if (skipTransition) {
        requestAnimationFrame(() => {
          setIsInitialized(true);
        });
      }
    }
  }, []);

  // Update on mount - skip transition for initial render
  useEffect(() => {
    const timer = setTimeout(() => updateIndicator(true), 50);
    return () => clearTimeout(timer);
  }, [updateIndicator]);

  // Use MutationObserver to detect when data-tab-active changes (like tabs.tsx does)
  // This provides smoother animation than relying on useEffect with activeTab
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const observer = new MutationObserver((mutations) => {
      const hasActiveChange = mutations.some(
        (m) => m.attributeName === 'data-tab-active'
      );
      if (hasActiveChange && isInitialized) {
        updateIndicator();
      }
    });

    observer.observe(nav, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-tab-active'],
    });

    return () => observer.disconnect();
  }, [updateIndicator, isInitialized]);

  // Update on resize
  useEffect(() => {
    const handleResize = () => updateIndicator(false);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateIndicator]);

  return (
    <div className="bg-muted rounded-lg p-1.5 inline-flex" data-tour="assignment-tabs">
      <nav ref={navRef} className="flex gap-1 relative">
        {/* Sliding indicator - positioned relative to nav */}
        <div
          data-indicator-id={uniqueId}
          className="absolute top-0 left-0 bg-background rounded-md border-2 border-black shadow-lg pointer-events-none"
          style={{
            ...indicatorStyle,
            transition: isInitialized
              ? 'transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1), width 200ms cubic-bezier(0.25, 0.1, 0.25, 1), opacity 150ms ease'
              : 'none',
          }}
        />
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-tab-active={activeTab === tab.id}
            data-tour={`assignment-tab-${tab.id}`}
            onClick={() => onTabChange(tab.id)}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 relative z-10 ${
              activeTab === tab.id
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground/80"
            }`}
          >
            <span className="mr-2">{iconMap[tab.id]}</span>
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
};
